import { Box, Divider, Flex, Stack } from "@chakra-ui/react";
import { useNavigate } from "react-router";

import { dayjs } from "@equidefi/i18n";
import { InquiryStatusBadge } from "@equidefi/portals/components/InquiryStatusBadge";
import { governmentIdLabel } from "@equidefi/shared";
import { blankIfNA } from "@equidefi/shared/helpers/string";
import type { DTO } from "@equidefi/shared/types";
import { Heading, Text } from "@equidefi/ui";

import { Row, SectionTitle, VaultContainer, VerificationResult } from "../../../components";
import { SectionButton } from "../../components";

type Props = { inquiry: DTO.FullInquiry };

// A section's data object can come back empty (e.g. a silent KYC that never
// collected a government ID), so only render a section when it has real values.
const hasValues = (obj?: object | null) => !!obj && Object.values(obj).some((value) => value != null && value !== "");

const InquiryKYCDetails = ({ inquiry }: Props) => {
  const navigate = useNavigate();

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <SectionButton onClick={() => navigate(-1)} iconDirection="left">
        Back To Identity Check Details
      </SectionButton>

      <Box bg="white" borderRadius="md" boxShadow="md" p="4">
        <Flex align="start" justify="space-between" gap="3" mb="3">
          <Box minW={0}>
            <Heading fontWeight="bold" as="h1" mb="1">
              {inquiry.display_name ?? "N/A"}
            </Heading>
            <Text textStyle="body2" color="equidefi.blue" mb="0">
              Last activity: {inquiry.modify_date ? dayjs(inquiry.modify_date).format("MMM Do, YYYY") : "N/A"}
            </Text>
          </Box>
          <InquiryStatusBadge status={inquiry.status} />
        </Flex>

        {hasValues(inquiry.personal_info) && (
          <>
            <Divider my="4" />
            <Stack spacing="2" mb="4">
              <SectionTitle>Personal Information</SectionTitle>
              <Box>
                <Row label="First Name" value={inquiry.personal_info?.first_name} />
                <Row label="Middle Name" value={inquiry.personal_info?.middle_name} />
                <Row label="Last Name" value={inquiry.personal_info?.last_name} />
              </Box>
              {inquiry.personal_info?.date_of_birth && (
                <Box pt="2">
                  <Row label="Date of Birth" value={dayjs(inquiry.personal_info.date_of_birth).format("MM/DD/YYYY")} />
                </Box>
              )}
            </Stack>
          </>
        )}

        {hasValues(inquiry.government_id) && (
          <>
            <Divider my="4" />
            <Stack spacing="2" mb="4">
              <SectionTitle>Government ID Details</SectionTitle>
              <Box>
                <Row label="ID Type" value={governmentIdLabel(inquiry.government_id?.type)} />
                <Row label="ID Number" value={inquiry.government_id?.number} />
              </Box>
              <Box pt="2">
                <Row label="Issuing Authority" value={inquiry.government_id?.issuing_authority} />
                <Row label="ID Issue Date" value={inquiry.government_id?.issue_date} />
                <Row label="ID Expiry Date" value={inquiry.government_id?.expiry_date} />
              </Box>
            </Stack>
          </>
        )}

        {hasValues(inquiry.home_address) && (
          <>
            <Divider my="4" />
            <Stack spacing="1" mb="4">
              <SectionTitle>Home Address</SectionTitle>
              <Row
                label="Address"
                value={
                  [
                    inquiry.home_address?.street_1,
                    inquiry.home_address?.street_2,
                    inquiry.home_address?.city,
                    inquiry.home_address?.state,
                    blankIfNA(inquiry.home_address?.postal_code),
                    inquiry.home_address?.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "N/A"
                }
              />
            </Stack>
          </>
        )}

        {inquiry.verification && (
          <>
            <Divider my="4" />
            <Stack spacing="2" mb="4">
              <SectionTitle>Verification Results</SectionTitle>
              <VerificationResult passed={!!inquiry.verification.passed} status={inquiry.status} />
            </Stack>
          </>
        )}

        {inquiry.decline_reason && (
          <>
            <Divider my="4" />
            <Stack spacing="2">
              <SectionTitle>Decline Reason</SectionTitle>
              <Box bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200" p="3">
                <Text fontSize="sm" mb="0">
                  {inquiry.decline_reason}
                </Text>
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </VaultContainer>
  );
};

export default InquiryKYCDetails;
